import * as React from 'react';
import {
    DisplayItemInputTitleNoMargin,
    DisplayItemInputTitleRequiredNoMargin
} from '../../../../styledComponents/DisplayItem';
import {GridContainer, GridItem} from '../../../../styledComponents/ResponsiveConvenience';
import {
    ApplyButton,
    ItemHeading,
    WideWhiteArea
} from '../../applications/common/resident/NatResidentApplicationsCommon';
import styled, {css, FlattenSimpleInterpolation} from 'styled-components'
import {DISASTER_TOPIC_CATEGORY} from '../../../../CodeMaster';
import PPRadioGroupForResident from '../../../../components/shared/forms/resident/PPRadioGroupForResident';
import {PPTextFieldForResident} from '../../../../components/shared/forms/resident/PPTextFieldForResident';
import {PPTextAreaForResident} from '../../../../components/shared/forms/resident/PPTextAreaForResident';
import {ChangeEvent, Component, ReactElement} from 'react';
import EventEmitterExResident from '../../../../components/shared/pageBaseComponents/EventEmitterExResident';
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents';
import {
    NatPostDisasterTopicRegisterRequest
} from '../repository/NatPostDisasterTopicRegister';
import {TopicInfo} from '../repository/NatGetDisasterTopic';
import {Colors} from '../../../../styledComponents/Constants';
import {Nullable} from '../../../../app/TypeHelpers';

interface Props {
    eventEmitter: EventEmitterExResident,

    topicInfo?: TopicInfo,
    submitButtonLabel: string
    onClickRegisterButton: (request: NatPostDisasterTopicRegisterRequest) => void
}

interface State {
    category: string,
    title: string,
    description: Nullable<string>,

    applyButtonDisabled: boolean,
    isLoadCompleted: boolean,
}

export default class NatResidentDisasterTopicForm extends Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            category: '',
            title: '',
            description: '',

            applyButtonDisabled: true,
            isLoadCompleted: false,
        }

        this.handleChangeDisasterTopicCategory = this.handleChangeDisasterTopicCategory.bind(this)
        this.handleChangeText = this.handleChangeText.bind(this)
        this.onClickRegisterButton = this.onClickRegisterButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {topicInfo} = this.props
        if (topicInfo) {
            this.setState({
                category: topicInfo.category,
                title: topicInfo.title,
                description: topicInfo.description,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        }
        this.setState({isLoadCompleted: true})
    }

    handleChangeDisasterTopicCategory(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            category: event.target.value,
        }, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {category, title} = this.state

        let applyButtonDisabled = false

        if (!category || !title) {
            applyButtonDisabled = true
        }

        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    onClickRegisterButton(): void {
        const {category, title, description} = this.state

        this.props.onClickRegisterButton({
            category: category,
            title: title,
            description: description,
        })
    }

    renderTopicForm(): ReactElement {
        const {eventEmitter} = this.props
        const {category, title, description} = this.state

        return (
            <>
                <ItemHeading>トピック</ItemHeading>
                <DescriptionArea>
                    <SectionInfo>
                        <DisplayItemInputTitleRequiredNoMargin>
                            カテゴリ
                        </DisplayItemInputTitleRequiredNoMargin>
                        <GridContainer>
                            <GridItem over={12}>
                                <PPRadioGroupForResident
                                    data={DISASTER_TOPIC_CATEGORY.values()}
                                    selectValue={category}
                                    onChange={this.handleChangeDisasterTopicCategory}
                                    eventKeys={['category']}
                                    eventEmitter={eventEmitter}
                                />
                            </GridItem>
                        </GridContainer>
                    </SectionInfo>
                    <SectionInfo>
                        <DisplayItemInputTitleRequiredNoMargin>
                            タイトル
                        </DisplayItemInputTitleRequiredNoMargin>
                        <PPTextFieldForResident
                            inputProps={{
                                name: 'title',
                                maxLength: 50,
                                value: title,
                                placeholder: '50文字以内'
                            }}
                            onChange={this.handleChangeText}
                            eventKeys={['title']}
                            eventEmitter={eventEmitter}
                        />
                    </SectionInfo>
                    <SectionInfo>
                        <DisplayItemInputTitleNoMarginWithOptionalLabel>
                            説明文
                        </DisplayItemInputTitleNoMarginWithOptionalLabel>
                        <DescriptionTextArea
                            inputProps={{
                                maxLength: 200,
                                name: 'description',
                                value: description,
                                placeholder: 'このトピックに投稿してほしい内容を記載することができます。（200文字以内）',
                            }}
                            onChange={this.handleChangeText}
                            eventKeys={['description']}
                            eventEmitter={eventEmitter}
                        />
                    </SectionInfo>
                </DescriptionArea>
            </>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled, isLoadCompleted} = this.state

        if (!isLoadCompleted) {
            return (<></>)
        }
        return (
            <>
                {this.renderTopicForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickRegisterButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const DescriptionArea = styled(WideWhiteArea)`
    padding: 20px 15px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const SectionInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const DescriptionTextArea = styled(PPTextAreaForResident)`
    height: 130px;
`

const OptionalLabelStyle: FlattenSimpleInterpolation = css`
    &:after {
        content: "任意";
        margin: 0 0 0 10px;
        padding: 3px;
        color: ${Colors.Body};
        font-size: 75%;
        background-color: ${Colors.AccentLight};
        white-space: nowrap;
    }
`
const DisplayItemInputTitleNoMarginWithOptionalLabel = styled(DisplayItemInputTitleNoMargin)`
    ${OptionalLabelStyle}
`